import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { type AppointmentType, appointmentTypes } from '@/data/scheduling-rules';
import {
  AlertCircle,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  Info,
  Search,
  Share2,
  X,
  XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';

export function AppointmentTypesTable() {
  const [searchTerm, setSearchTerm] = useState('');
  const [durationFilter, setDurationFilter] = useState<string>('all');
  const [schedulableFilter, setSchedulableFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [sortConfig, setSortConfig] = useState<{
    key: keyof AppointmentType;
    direction: 'asc' | 'desc';
  } | null>(null);

  const toggleRowExpansion = (ehrId: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ehrId)) {
        newSet.delete(ehrId);
      } else {
        newSet.add(ehrId);
      }
      return newSet;
    });
  };

  const handleSort = (key: keyof AppointmentType) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const filteredAndSortedAppointments = useMemo(() => {
    let filtered = appointmentTypes.filter((apt) => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        apt.name.toLowerCase().includes(searchLower) ||
        apt.abbreviation.toLowerCase().includes(searchLower);

      // Duration filter
      const matchesDuration = durationFilter === 'all' || apt.durationCategory === durationFilter;

      // Schedulable filter
      const matchesSchedulable =
        schedulableFilter === 'all' ||
        (schedulableFilter === 'yes' && apt.doesTrapezeSchedule) ||
        (schedulableFilter === 'no' && !apt.doesTrapezeSchedule);

      // Provider filter
      const matchesProvider =
        providerFilter === 'all' ||
        (providerFilter === 'doshi' && apt.canScheduleForDoshi) ||
        (providerFilter === 'kligman' && apt.canScheduleForKligman) ||
        (providerFilter === 'berliner' && apt.canScheduleForBerliner);

      return matchesSearch && matchesDuration && matchesSchedulable && matchesProvider;
    });

    // Sort
    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (typeof aValue === 'boolean') {
          return sortConfig.direction === 'asc'
            ? (aValue ? 1 : -1) - (bValue ? 1 : -1)
            : (bValue ? 1 : -1) - (aValue ? 1 : -1);
        }

        if (typeof aValue === 'string') {
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue as string)
            : (bValue as string).localeCompare(aValue);
        }

        return 0;
      });
    }

    return filtered;
  }, [searchTerm, durationFilter, schedulableFilter, providerFilter, sortConfig]);

  const clearFilters = () => {
    setSearchTerm('');
    setDurationFilter('all');
    setSchedulableFilter('all');
    setProviderFilter('all');
  };

  const hasActiveFilters =
    searchTerm ||
    durationFilter !== 'all' ||
    schedulableFilter !== 'all' ||
    providerFilter !== 'all';

  const getFilterSummary = () => {
    const parts = [];

    if (searchTerm) {
      parts.push(`matching "${searchTerm}"`);
    }

    if (schedulableFilter === 'yes') {
      parts.push('schedulable by Iris');
    } else if (schedulableFilter === 'no') {
      parts.push('requiring manual scheduling');
    }

    if (providerFilter !== 'all') {
      const providerName =
        providerFilter === 'doshi'
          ? 'Dr. Doshi'
          : providerFilter === 'kligman'
            ? 'Dr. Kligman'
            : 'Dr. Berliner';
      parts.push(`available for ${providerName}`);
    }

    if (durationFilter !== 'all') {
      parts.push(`with ${durationFilter} duration`);
    }

    return parts.length ? `Showing appointment types ${parts.join(', ')}` : 'All appointment types';
  };

  const handleExport = (format: string, data: string) => {
    // Placeholder function - doesn't actually export
    console.log(`Exporting ${data} as ${format}`, filteredAndSortedAppointments);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">Quick Actions</h2>
        <p className="text-sm text-muted-foreground">
          Common tasks for understanding appointment type scheduling rules
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => {
            setSchedulableFilter('yes');
            setSearchTerm('');
            setDurationFilter('all');
            setProviderFilter('all');
          }}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
                <CheckCircle className="h-4 w-4" />
              </div>
              <Badge className="bg-green-100 text-green-700 text-xs">
                {appointmentTypes.filter((apt) => apt.doesTrapezeSchedule).length}
              </Badge>
            </div>
            <h3 className="font-medium text-sm">Schedulable Types</h3>
            <p className="text-xs text-muted-foreground mt-1">Appointment types Iris can handle</p>
          </CardContent>
        </Card>

        <Card
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => {
            setSchedulableFilter('no');
            setSearchTerm('');
            setDurationFilter('all');
            setProviderFilter('all');
          }}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
                <XCircle className="h-4 w-4" />
              </div>
              <Badge variant="outline" className="border-red-200 text-red-600 text-xs">
                {appointmentTypes.filter((apt) => !apt.doesTrapezeSchedule).length}
              </Badge>
            </div>
            <h3 className="font-medium text-sm">Needs Manual Scheduling</h3>
            <p className="text-xs text-muted-foreground mt-1">Requires staff intervention</p>
          </CardContent>
        </Card>

        <Card
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => {
            setDurationFilter('long');
            setSchedulableFilter('all');
            setSearchTerm('');
            setProviderFilter('all');
          }}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
                <Clock className="h-4 w-4" />
              </div>
              <Badge variant="outline" className="text-xs">
                Long Duration
              </Badge>
            </div>
            <h3 className="font-medium text-sm">Complex Appointments</h3>
            <p className="text-xs text-muted-foreground mt-1">Require special scheduling rules</p>
          </CardContent>
        </Card>
      </div>

      {/* Results Summary */}
      {(hasActiveFilters || searchTerm) && (
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
                  {filteredAndSortedAppointments.length}
                </div>
                <div>
                  <p className="text-sm font-medium">{getFilterSummary()}</p>
                  <p className="text-xs text-muted-foreground">
                    {filteredAndSortedAppointments.length} of {appointmentTypes.length} appointment
                    types
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Advanced Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Appointment Types</CardTitle>
              <CardDescription>
                Detailed view with search and advanced filtering • {appointmentTypes.length} total
                types
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExport('csv', 'filtered-results')}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Filtered Results (CSV)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('xlsx', 'filtered-results')}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Filtered Results (Excel)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleExport('pdf', 'detailed-report')}>
                    <FileText className="h-4 w-4 mr-2" />
                    Detailed Report (PDF)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('csv', 'all-appointment-types')}>
                    <Share2 className="h-4 w-4 mr-2" />
                    All Types (CSV)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear filters
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search appointment types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={durationFilter} onValueChange={setDurationFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Durations</SelectItem>
                <SelectItem value="short">Short</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="long">Long</SelectItem>
                <SelectItem value="other">Other</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>

            <Select value={schedulableFilter} onValueChange={setSchedulableFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Schedulable" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="yes">Schedulable</SelectItem>
                <SelectItem value="no">Non-Schedulable</SelectItem>
              </SelectContent>
            </Select>

            <Select value={providerFilter} onValueChange={setProviderFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Providers</SelectItem>
                <SelectItem value="doshi">Dr. Doshi</SelectItem>
                <SelectItem value="kligman">Dr. Kligman</SelectItem>
                <SelectItem value="berliner">Dr. Berliner</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" />
              Showing {filteredAndSortedAppointments.length} of {appointmentTypes.length}{' '}
              appointment types
            </div>
            {hasActiveFilters && (
              <div className="text-xs text-muted-foreground">
                Export will include only filtered results
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12" />
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {sortConfig?.key === 'name' &&
                        (sortConfig.direction === 'asc' ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead>Abbreviation</TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('durationCategory')}
                  >
                    <div className="flex items-center gap-1">
                      Duration
                      {sortConfig?.key === 'durationCategory' &&
                        (sortConfig.direction === 'asc' ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('doesTrapezeSchedule')}
                  >
                    <div className="flex items-center gap-1">
                      Schedulable
                      {sortConfig?.key === 'doesTrapezeSchedule' &&
                        (sortConfig.direction === 'asc' ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">Dr. Doshi</TableHead>
                  <TableHead className="text-center">Dr. Kligman</TableHead>
                  <TableHead className="text-center">Dr. Berliner</TableHead>
                  <TableHead className="w-12">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Click rows for more details</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedAppointments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2">
                        <Search className="h-8 w-8 text-muted-foreground/50" />
                        <p className="text-sm font-medium text-muted-foreground">
                          No appointment types found
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Try adjusting your search or filters
                        </p>
                        {hasActiveFilters && (
                          <Button variant="ghost" size="sm" onClick={clearFilters} className="mt-2">
                            <X className="h-4 w-4 mr-1" />
                            Clear all filters
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedAppointments.map((apt) => (
                    <>
                      <TableRow
                        key={apt.ehrId}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleRowExpansion(apt.ehrId)}
                      >
                        <TableCell>
                          {expandedRows.has(apt.ehrId) ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{apt.name}</TableCell>
                        <TableCell>
                          {apt.abbreviation && (
                            <Badge variant="outline" className="font-mono text-xs">
                              {apt.abbreviation}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {apt.durationCategory && (
                            <Badge variant="secondary" className="text-xs">
                              {apt.durationCategory}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {apt.doesTrapezeSchedule ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                              <Check className="h-3 w-3 mr-1" />
                              Yes
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              <X className="h-3 w-3 mr-1" />
                              No
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {renderProviderStatus(apt.canScheduleForDoshi)}
                        </TableCell>
                        <TableCell className="text-center">
                          {renderProviderStatus(apt.canScheduleForKligman)}
                        </TableCell>
                        <TableCell className="text-center">
                          {renderProviderStatus(apt.canScheduleForBerliner)}
                        </TableCell>
                        <TableCell>
                          {!apt.doesTrapezeSchedule && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <AlertCircle className="h-4 w-4 text-amber-600" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Requires transfer to staff</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </TableCell>
                      </TableRow>
                      {expandedRows.has(apt.ehrId) && (
                        <TableRow>
                          <TableCell colSpan={9} className="bg-muted/30">
                            <div className="p-4 space-y-3">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                  <Label className="text-xs text-muted-foreground">EHR ID</Label>
                                  <p className="font-mono text-sm">{apt.ehrId}</p>
                                </div>
                                <div>
                                  <Label className="text-xs text-muted-foreground">
                                    Scheduling Status
                                  </Label>
                                  <p className="text-sm">
                                    {apt.doesTrapezeSchedule
                                      ? 'Iris can schedule'
                                      : 'Manual scheduling required'}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-xs text-muted-foreground">
                                    Duration Category
                                  </Label>
                                  <p className="text-sm">
                                    {apt.durationCategory || 'Not specified'}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-xs text-muted-foreground">
                                    Provider Restrictions
                                  </Label>
                                  <p className="text-sm">{getProviderRestrictionSummary(apt)}</p>
                                </div>
                              </div>

                              {!apt.doesTrapezeSchedule && (
                                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                                  <div className="flex items-start gap-2">
                                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                                    <div className="text-sm">
                                      <p className="font-medium text-amber-900">
                                        Transfer Required
                                      </p>
                                      <p className="text-amber-700 mt-1">
                                        This appointment type cannot be scheduled by Iris. The
                                        patient will be transferred to office staff or PCC for
                                        manual scheduling.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {apt.durationCategory === 'long' && (
                                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                                  <div className="flex items-start gap-2">
                                    <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                                    <div className="text-sm">
                                      <p className="font-medium text-blue-900">
                                        Scheduling Constraints
                                      </p>
                                      <ul className="text-blue-700 mt-1 space-y-1">
                                        <li>• Cannot be scheduled back-to-back</li>
                                        <li>• Not within 30 mins before lunch</li>
                                        <li>• Not within 1 hour of end of day</li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function renderProviderStatus(canSchedule: boolean) {
  if (canSchedule) {
    return <Check className="h-4 w-4 text-green-600 mx-auto" />;
  }
  return <X className="h-4 w-4 text-gray-300 mx-auto" />;
}

function getProviderRestrictionSummary(apt: AppointmentType): string {
  const providers = [];
  if (apt.canScheduleForDoshi) providers.push('Doshi');
  if (apt.canScheduleForKligman) providers.push('Kligman');
  if (apt.canScheduleForBerliner) providers.push('Berliner');

  if (providers.length === 0) return 'No providers';
  if (providers.length === 3) return 'All providers';
  return `${providers.join(', ')} only`;
}
